import cn from "classnames";
import { LatLngTuple } from "leaflet";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Polygon } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import * as Yup from "yup";

import {
  KYIV_COORDINATES,
  LayerAlias,
  MapModes,
  UNITS_LIMIT,
  UnitTypes,
  UserPolices
} from "constant";
import features from "features";

import { ModalTypes } from "components/modals/interfaces";
import { useLabelingContext } from "context/LabelingContextProvider";
import { useMapContext } from "context/MapContextProvider";
import usePolicy from "hooks/usePolicy";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";
import { LocalStorageProvider } from "services/LocalStorageProvider";
import { MapStateInterface } from "../interfaces";
import usePointHandler from "../usePointHandler";

import FormField from "components/inputs/FormField/FormField";
import ZoomBtns from "components/ZoomBtns/ZoomBtns";
import usePolygonHandler from "scenes/MapPage/usePolygonHandler";
import MapConfirmControls from "../components/MapConfirmControls/MapConfirmControls";
import MapLayersItems from "../components/MapLayersItems/MapLayersItems";
import MapMarkerDetails from "../components/MapMarkerDetails/MapMarkerDetails";
import MapMissionItems from "../components/MapMissionItems/MapMissionItems";
import MapPointBuilder from "../components/MapPointBuilder/MapPointBuilder";
import MapPolygonBuilder from "../components/MapPolygonBuilder/MapPolygonBuilder";
import MapProjectName from "../components/MapProjectName/MapProjectName";
import MapToolbar from "../components/MapToolbar/MapToolbar";
import MapTools from "../components/MapTools/MapTools";
import MapVehicleBuilder from "../components/MapVehicleBuilder/MapVehicleBuilder";
import MapEventHandler from "../MapEventHandler";

import styles from "./map-page.module.scss";

const UNITS_SKIP_COUNT = 0;

const MapPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { id } = useParams();
  const { hasPolicy } = usePolicy();
  const { mapRef } = useMapContext();
  const { clearPolygonPoints, cropPolygon, generateLabelingData } =
    useLabelingContext();
  const { createPoint, addLabeling } = usePointHandler();

  const [perimeterBounds, setPerimeterBounds] = useState<LatLngTuple[] | null>(
    null
  );

  const {
    title,
    aries,
    mapMode,
    isProjectLoading,
    isLayerCreateLoading,
    isMarkersLoading,
    isMarkerCreateLoading
  } = useSelector<RootStateInterface, MapStateInterface>((state) => state.map);

  const {
    unitsCollection,
    // TODO
    // On open layer manager modal this action fetch units, its change isProcessing and show process modal and close providers modal
    // isFetchUnitsLoading,
    creationPolygonParameters
  } = useSelector<RootStateInterface, UnitsManagerStateInterface>(
    (state) => state.unitsManager
  );
  const { layers } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const { openPolygonCreationModal, activePolygonsListItem } =
    usePolygonHandler(creationPolygonParameters);

  const isProcessing = useMemo(
    () =>
      isProjectLoading ||
      isLayerCreateLoading ||
      isMarkersLoading ||
      // isFetchUnitsLoading ||
      isMarkerCreateLoading,
    [
      isLayerCreateLoading,
      isMarkersLoading,
      isProjectLoading,
      isMarkerCreateLoading
      // isFetchUnitsLoading
    ]
  );

  // const activeMarker = useMemo(
  //   () => selectedMarkers.find((marker) => marker.id === markerDetails?.id),
  //   [markerDetails?.id, selectedMarkers]
  // );
  // const thermalMarkers = useMemo(() => {
  //   return selectedMarkers.filter((item) => item.layer === LayerAlias.THERMAL);
  // }, [selectedMarkers]);

  const defaultActiveLayer = useMemo(() => {
    return layers?.find((item) => item.providers?.length > 0);
  }, [layers]);

  const mapCenter = useMemo(() => {
    const center = defaultActiveLayer?.providers?.[0]?.data?.center;
    return center ? { lng: center[0], lat: center[1] } : null;
  }, [defaultActiveLayer]);

  const mapZoom = useMemo(() => {
    const zoom = defaultActiveLayer?.providers?.[0]?.data?.center?.[2];
    const maxNativeZoom = defaultActiveLayer?.providers?.[0]?.data?.maxzoom;

    return {
      zoom: zoom > 0 ? zoom : 9,
      maxNativeZoom: maxNativeZoom > 0 ? maxNativeZoom : 26
    };
  }, [defaultActiveLayer]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get("page");

    LocalStorageProvider.routes.setProjectsCurrentPage(page);

    if (hasPolicy(UserPolices.PROJECT_READ))
      dispatch(features.map.actions.fetchProjectRequest({ params: { id } }));

    if (hasPolicy(UserPolices.PROJECT_PROVIDER_UNIT_LIST)) {
      dispatch(
        features.unitsManager.actions.fetchUnitsRequest({
          params: {
            projectId: id,
            limit: UNITS_LIMIT,
            skip: UNITS_SKIP_COUNT,
            type: UnitTypes.POLYGON,
            alias: LayerAlias.MANUAL
          }
        })
      );

      dispatch(
        features.unitsManager.actions.fetchUnitsRequest({
          params: {
            projectId: id,
            limit: 9999999999,
            skip: UNITS_SKIP_COUNT,
            type: UnitTypes.POINT,
            alias: LayerAlias.MANUAL
          }
        })
      );
    }
  }, [dispatch, hasPolicy, id]);

  useEffect(() => {
    if (isProcessing)
      dispatch(
        features.modal.actions.showModal({
          modalType: ModalTypes.PRELOADER,
          modalProps: {
            title: "Processing",
            loading: true,
            isDisableClose: true
          }
        })
      );
    else dispatch(features.modal.actions.hideModal());
  }, [dispatch, isProcessing]);

  useEffect(() => {
    return () => {
      dispatch(features.modal.actions.hideModal());
      dispatch(features.map.actions.restoreDefaultState());
      dispatch(features.layersManager.actions.restoreDefaultState());
      dispatch(features.unitsManager.actions.restoreDefaultState());
      dispatch(features.missionsManager.actions.restoreDefaultState());
    };
  }, [dispatch, location.key]);

  const setView = (...args) => {
    if (mapRef.current) {
      mapRef.current.setView(...args);
    }
  };

  const createPerimeter = (
    point1: LatLngTuple,
    point2: LatLngTuple,
    point3: LatLngTuple,
    point4: LatLngTuple
  ) => {
    const bounds: LatLngTuple[] = [point1, point2, point3, point4];
    setPerimeterBounds(bounds);

    const avgLat =
      bounds.reduce((sum, item) => sum + item[0], 0) / bounds.length;
    const avgLng =
      bounds.reduce((sum, item) => sum + item[1], 0) / bounds.length;

    mapRef.current.setView({ lat: avgLat, lng: avgLng }, 18);
  };

  const removePerimeter = () => {
    setPerimeterBounds(null);
  };

  useEffect(() => {
    (window as any).createPerimeter = createPerimeter;
    (window as any).removePerimeter = removePerimeter;
  }, []);

  // TODO
  // useEffect(() => {
  //   const audio = new Audio(NotificationSound);
  //   audio.play().catch((error) => {
  //     console.error("Failed to play sound:", error);
  //   });
  // }, []);

  const onDeclineDrawPolygonClick = useCallback(() => {
    switch (mapMode) {
      case MapModes.LABEL_POINT_POLYGON_CREATION: {
        clearPolygonPoints();
        break;
      }
      default:
        dispatch(
          features.unitsManager.actions.clearCreationPolygonParameters()
        );
        dispatch(features.unitsManager.actions.clearCreationPolygonHistory());
        break;
    }
  }, [clearPolygonPoints, dispatch, mapMode]);

  const onAcceptDrawPolygonClick = useCallback(async () => {
    switch (mapMode) {
      case MapModes.POLYGON_CREATION:
        openPolygonCreationModal(creationPolygonParameters);
        break;
      case MapModes.MISSION_POLYGON_CREATION: {
        dispatch(
          features.modal.actions.showModal({
            modalType: ModalTypes.FORM,
            modalProps: {
              title: "Create mission",
              description: "Please enter mission name.",
              initialValues: { name: "" },
              onSubmit: (val) => {
                dispatch(
                  features.missionsManager.actions.createMissionRequest({
                    params: { projectId: id },
                    fields: {
                      name: val.name,
                      data: {
                        polygon: {
                          coordinates: [
                            creationPolygonParameters.geo_data.coordinates
                          ],
                          type: creationPolygonParameters.geo_data.type
                        },
                        vehicles: []
                      }
                    },
                    onSuccess: () => {
                      dispatch(features.modal.actions.hideModal());

                      dispatch(
                        features.map.actions.setMapMode(MapModes.DEFAULT)
                      );
                      //TODO
                      onDeclineDrawPolygonClick();
                    }
                  })
                );
              },
              validationSchema: Yup.object().shape({
                name: Yup.string().required("Mission name is required")
              }),
              acceptButtonLabel: "Send",
              formContent: (
                <>
                  <FormField name="name" placeholder="Mission name" />
                </>
              )
            }
          })
        );
        // dispatch(features.modal.actions.showSuccessModal({}));

        break;
      }
      case MapModes.LABEL_POINT_POLYGON_CREATION: {
        const {
          base64,
          bbox,
          point: coordinates
        } = await generateLabelingData();

        createPoint({
          coordinates,

          onCreatePointSuccess: (res) => {
            addLabeling({ base64Image: base64, unitId: res.data.id, bbox });
            clearPolygonPoints();
          }
        });

        break;
      }
      default:
        break;
    }
  }, [
    addLabeling,
    clearPolygonPoints,
    createPoint,
    creationPolygonParameters,
    dispatch,
    generateLabelingData,
    id,
    mapMode,
    onDeclineDrawPolygonClick,
    openPolygonCreationModal
  ]);

  // useEffect(() => {
  //   const audio = new Audio(NotificationSound);
  //   console.log(audio);

  //   // audio.play().catch((error) => {
  //   //   console.error("Failed to play sound:", error);
  //   // });
  // }, [completionMessages]);

  return (
    <div
      className={cn(styles["map-page"], {
        [styles["map-page__draw-mode"]]:
          mapMode === MapModes.POLYGON_CREATION ||
          mapMode === MapModes.MISSION_POLYGON_CREATION ||
          mapMode === MapModes.LABEL_POINT_POLYGON_CREATION
      })}
    >
      {title && (
        <MapProjectName name={title} className={styles["project-title"]} />
      )}

      <MapTools
        layers={layers}
        aries={aries}
        setView={setView}
        unitsCollection={unitsCollection}
      />
      {title && (
        <MapContainer
          zoom={mapZoom.zoom}
          zoomControl={false}
          center={mapCenter ? mapCenter : KYIV_COORDINATES}
          className={styles["map"]}
          pmIgnore={false}
          maxZoom={28}
          ref={mapRef}
          preferCanvas={true}
        >
          <MapEventHandler
            mode={mapMode}
            activePolygon={activePolygonsListItem}
          />

          <MapLayersItems mapZoom={mapZoom} />

          <MapMissionItems />

          <ZoomBtns
            zoomIn={() => mapRef.current.zoomIn()}
            zoomOut={() => mapRef.current.zoomOut()}
          />
          {(mapMode === MapModes.POLYGON_CREATION ||
            mapMode === MapModes.MISSION_POLYGON_CREATION) && (
            <MapPolygonBuilder />
          )}
          {mapMode === MapModes.LABEL_POINT_POLYGON_CREATION && (
            <MapPointBuilder />
          )}
          {mapMode === MapModes.MISSION_VEHICLE_POINT_CREATION && (
            <MapVehicleBuilder />
          )}
          {(((mapMode === MapModes.POLYGON_CREATION ||
            MapModes.MISSION_POLYGON_CREATION) &&
            !creationPolygonParameters.id &&
            creationPolygonParameters.geo_data.coordinates.length > 2) ||
            (mapMode === MapModes.LABEL_POINT_POLYGON_CREATION &&
              cropPolygon?.length > 0)) && (
            <MapConfirmControls
              onAccept={onAcceptDrawPolygonClick}
              onDecline={onDeclineDrawPolygonClick}
            />
          )}
          <MapToolbar />

          {perimeterBounds && (
            <Polygon positions={perimeterBounds} color="blue" />
          )}
        </MapContainer>
      )}
      <MapMarkerDetails />
    </div>
  );
};

export default MapPage;
